import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { useTemplates } from '../context/TemplateContext';
import { AlertBanner, ConfirmDialog } from '../components/ui';
import ViewLayout2 from '../components/ui/ViewLayout2';
import StatBar from '../components/ui/StatBar';
import { Logger } from '../utils/logger';
import { llmStorage } from '../services/storageService';

const log = Logger.create('LLMGeneratorView');

// Tab Components
import LLMGenerateTab from '../components/llm-generator/LLMGenerateTab';
import LLMPreviewTab from '../components/llm-generator/LLMPreviewTab';
import LLMSettingsTab from '../components/llm-generator/LLMSettingsTab2';
import { PromptClipboardModal } from '../components/llm-generator';

// Wizard Mode
import WizardMode from '../components/llm-generator/wizard/WizardMode';

// Custom Hooks
import {
  useLLMConfig,
  useFileUpload,
  useTopicTags,
  useLLMGeneration,
  useQuestionOperations,
  useQuestionValidation,
  useQuestionEditor,
  useQuestionPersistence
} from '../hooks/llm-generator';

// Utilities
import { formatFileSize } from '../utils/llm-generator/fileParser';
import { 
  getDefaultSystemPrompt as getDefaultSystemPromptUtil,
  buildSystemPromptFromTemplate 
} from '../utils/llm-generator/promptBuilder';
import { supportsVision } from '../utils/llm-generator/apiClients';

/**
 * LLMGeneratorView - LLM Quiz Generator with modular hooks
 * 
 * This view orchestrates the LLM Quiz Generator using modular hooks
 * for better maintainability and testing.
 * 
 * Tab structure:
 * - Generieren: Simple upload + generate (quick workflow)
 * - Wizard: Document wizard with preview/chunking (for large docs)
 * - Vorschau: View/edit generated questions
 * - Einstellungen: LLM configuration
 */

const TABS = [
  { id: 'simple', label: 'Generieren' },
  { id: 'wizard', label: 'Wizard' },
  { id: 'preview', label: 'Vorschau' },
  { id: 'settings', label: 'Einstellungen' }
];

export default function LLMGeneratorView() {
  const { user } = useUnifiedAuth();
  const { loadTemplates } = useTemplates();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Navigation context - support both state and query params
  const returnTo = location.state?.returnTo;
  const templateId = location.state?.templateId;
  const templateName = location.state?.templateName;
  const courseId = location.state?.courseId || searchParams.get('courseId');
  
  // ============================================
  // UI State (minimal - only what's needed for the view)
  // ============================================
  const [activeTab, setActiveTab] = useState('simple');
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [clipboardPrompt, setClipboardPrompt] = useState({ system: '', user: '' });
  const [hasUnsavedSettings, setHasUnsavedSettings] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState(null);
  
  // Generation configuration (shared between tabs)
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [difficulty, setDifficulty] = useState('medium');
  const [includeCodeBlocks, setIncludeCodeBlocks] = useState(true);
  const [multipleChoiceMode, setMultipleChoiceMode] = useState('single');
  const [appendToExisting, setAppendToExisting] = useState(false);
  const [userGuidance, setUserGuidance] = useState('');
  
  // PDF processing mode: 'local' (extract text), 'native' (API upload), 'images' (vision)
  const [pdfProcessingMode, setPdfProcessingMode] = useState('local');
  const [imageQuality, setImageQuality] = useState('medium');  
  // ============================================
  // Custom Hooks
  // ============================================
  
  // LLM Configuration (provider, model, temperature etc.)
  const config = useLLMConfig();
  
  // File Upload
  const fileUpload = useFileUpload();
  
  // Topic Tags
  const tags = useTopicTags();
  
  // Check for PDF files and vision support
  const hasPdfFiles = React.useMemo(() => 
    fileUpload.uploadedFiles?.some(f => f.name?.toLowerCase().endsWith('.pdf')) || false,
    [fileUpload.uploadedFiles]
  );
  
  const modelSupportsVision = React.useMemo(() => 
    supportsVision(config.model || '', config.llmProvider || ''),
    [config.model, config.llmProvider]
  );
  
  // Question Generation - pass config as object
  const generation = useLLMGeneration({
    llmProvider: config.llmProvider,
    apiKey: config.apiKey,
    model: config.model,
    baseUrl: config.baseUrl,
    temperature: config.temperature,
    maxTokens: config.maxTokens,
    systemPrompt: config.systemPrompt,
    enableJsonRepair: config.enableJsonRepair
  });
  
  // Question Operations (reroll, variation, shuffle)
  const operations = useQuestionOperations(
    {
      llmProvider: config.llmProvider,
      apiKey: config.apiKey,
      model: config.model,
      baseUrl: config.baseUrl,
      temperature: config.temperature,
      maxTokens: config.maxTokens,
      systemPrompt: config.systemPrompt
    },
    generation.generatedQuestions,
    generation.setGeneratedQuestions
  );
  
  // Question Validation
  const validation = useQuestionValidation(generation.generatedQuestions);
  
  // Question Editor
  const editor = useQuestionEditor(
    generation.generatedQuestions,
    generation.setGeneratedQuestions
  );
  
  // Question Persistence (Firebase)
  const persistence = useQuestionPersistence(user?.uid);
  
  // ============================================
  // Effects
  // ============================================
  
  // Suppress browser extension errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.toString().includes('message channel closed')) return;
      originalError.apply(console, args);
    };
    return () => { console.error = originalError; };
  }, []);
  
  // Auto-select template if coming from TemplateEditor
  useEffect(() => {
    if (templateId && persistence.existingSessions.length > 0) {
      const exists = persistence.existingSessions.some(s => s.id === templateId);
      if (exists) {
        persistence.setSelectedSession(templateId);
        log.debug(`Auto-selected template: ${templateId}`);
      }
    }
  }, [templateId, persistence.existingSessions]);
  
  // ============================================
  // Handlers
  // ============================================
  
  // Tab change with unsaved settings warning
  const handleTabChange = useCallback((newTab) => {
    // If leaving settings tab with unsaved changes, show warning
    if (activeTab === 'settings' && hasUnsavedSettings && newTab !== 'settings') {
      setPendingTabChange(newTab);
      return;
    }
    setActiveTab(newTab);
  }, [activeTab, hasUnsavedSettings]);
  
  // Confirm tab change (discard unsaved settings)
  const confirmTabChange = useCallback(() => {
    if (pendingTabChange) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
      setHasUnsavedSettings(false);
    }
  }, [pendingTabChange]);
  
  // Cancel tab change
  const cancelTabChange = useCallback(() => {
    setPendingTabChange(null);
  }, []);
  
  // Generate questions
  const handleGenerate = useCallback(async (preSelectedContent = null) => {
    // For clipboard provider, show prompt modal
    if (config.llmProvider === 'clipboard') {
      const prompts = generation.buildClipboardPrompt({
        fileContents: preSelectedContent
          ? [{ name: 'Vorausgewählter Inhalt', content: preSelectedContent }]
          : fileUpload.fileContents,
        numberOfQuestions,
        difficulty,
        includeCodeBlocks,
        multipleChoiceMode,
        userGuidance,
        topicTags: tags.topicTags,
        preSelectedContent
      });
      setClipboardPrompt(prompts);
      setShowPromptModal(true);
      return;
    }
    
    // Check API key
    if (!config.apiKey && config.llmProvider !== 'custom') {
      setError('Bitte API-Key in den Einstellungen eingeben');
      setActiveTab('settings');
      return;
    }
    
    const result = await generation.generateQuestions({
      fileContents: fileUpload.fileContents,
      numberOfQuestions,
      difficulty,
      includeCodeBlocks,
      multipleChoiceMode,
      userGuidance,
      topicTags: tags.topicTags,
      preSelectedContent,
      appendToExisting
    });
    
    if (result.success) {
      setActiveTab('preview');
    } else {
      setError(result.error);
    }
  }, [config, fileUpload.fileContents, numberOfQuestions, difficulty, includeCodeBlocks, multipleChoiceMode, userGuidance, tags.topicTags, appendToExisting, generation]);
  
  // Handle file upload with logging
  const handleFileUpload = useCallback(async (e) => {
    const result = await fileUpload.handleFileUpload(e, generation.addLog);
    if (!result.success && result.errors.length > 0) {
      setError(result.errors.join('\n'));
    }
  }, [fileUpload, generation.addLog]);
  
  // Handle template selection
  const handleTemplateSelect = useCallback((templateId) => {
    config.handleTemplateSelect(
      templateId,
      tags.topicTags,
      tags.setTopicTags,
      setIncludeCodeBlocks
    );
  }, [config, tags.topicTags, tags.setTopicTags]);
  
  // Save to session with notification
  const handleSaveToSession = useCallback(async (sessionId, newName = null) => {
    const result = await persistence.saveToSession(
      sessionId,
      generation.generatedQuestions,
      newName
    );
    
    if (result.success) {
      generation.addLog(`\n✅ ${result.message}\n`);
      setNotification({ type: 'success', message: result.message });
      // Refresh templates in context so Dashboard shows the new template
      loadTemplates();
    } else {
      setError(result.error);
      setNotification({ type: 'error', message: result.error });
    }
  }, [persistence, generation, loadTemplates]);
  
  // Download questions
  const handleDownloadQuestions = useCallback(() => {
    persistence.downloadQuestions(generation.generatedQuestions);
  }, [persistence, generation.generatedQuestions]);
  
  // Import questions to preview
  const handleImportQuestions = useCallback((questions) => {
    generation.setGeneratedQuestions(prev => [...prev, ...questions]);
    setActiveTab('preview');
  }, [generation]);
  
  // Reroll with options
  const handleRerollQuestion = useCallback(async (index) => {
    const result = await operations.rerollQuestion(index, {
      includeCodeBlocks,
      useConversationHistory: config.useConversationHistory
    });
    if (!result.success) {
      setError(result.error);
    }
  }, [operations, includeCodeBlocks, config.useConversationHistory]);
  
  // Generate variation with options
  const handleGenerateVariation = useCallback(async (index) => {
    const result = await operations.generateVariation(index, {
      includeCodeBlocks,
      useConversationHistory: config.useConversationHistory
    });
    if (!result.success) {
      setError(result.error);
    }
  }, [operations, includeCodeBlocks, config.useConversationHistory]);
  
  // Rephrase with options
  const handleRephraseQuestion = useCallback(async (index) => {
    const result = await operations.rephraseQuestion(index, {
      includeCodeBlocks,
      useConversationHistory: config.useConversationHistory
    });
    if (!result.success) {
      setError(result.error);
    }
  }, [operations, includeCodeBlocks, config.useConversationHistory]);
  
  // Reset settings
  const handleResetSettings = useCallback(() => {
    config.resetConfig();
    llmStorage.remove('generatorSettings');
    setShowResetConfirm(false);
  }, [config]);
  
  // Refinement chat (placeholder - needs separate implementation)
  const openRefinementChat = useCallback((index) => {
    log.debug('Open refinement chat for question', index);
  }, []);
  
  // ============================================
  // Stats & UI Config
  // ============================================
  
  const stats = [
    { value: fileUpload.fileCount, label: 'Dateien', color: 'primary' },
    { value: generation.questionCount, label: 'Fragen', color: 'success' },
    { value: validation.questionsWithErrors, label: 'Fehler', color: validation.questionsWithErrors > 0 ? 'danger' : 'secondary' }
  ];
  
  const tabsWithCounts = TABS.map(tab => {
    if (tab.id === 'preview') return { ...tab, count: generation.questionCount };
    return tab;
  });
  
  const primaryAction = (activeTab === 'simple') ? {
    label: 'Generieren',
    onClick: () => handleGenerate(),
    disabled: generation.isGenerating || (!fileUpload.hasFiles && !userGuidance?.trim())
  } : (activeTab === 'preview' && generation.hasQuestions) ? {
    label: 'Speichern',
    dropdown: [
      { label: 'Als JSON herunterladen', onClick: handleDownloadQuestions },
      ...(persistence.hasTemplates ? [
        { label: 'In Vorlage speichern...', onClick: () => {} }
      ] : [])
    ]
  } : null;
  
  // Handle back navigation - pass questions back to TemplateEditor if applicable
  const handleBack = useCallback(() => {
    if (returnTo && templateId && generation.hasQuestions) {
      // Navigate back to TemplateEditor with generated questions
      navigate(returnTo, {
        state: {
          generatedQuestions: generation.generatedQuestions,
          fromLLMGenerator: true
        }
      });
    } else {
      navigate(returnTo || '/dashboard');
    }
  }, [returnTo, templateId, generation.hasQuestions, generation.generatedQuestions, navigate]);
  
  // ============================================
  // Render
  // ============================================
  
  return (
    <ViewLayout2
      title="LLM Quiz-Generator"
      onBack={handleBack}
      tabs={tabsWithCounts}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      primaryAction={primaryAction}
      maxWidth="6xl"
    >
      {/* Unsaved Settings Warning Dialog */}
      <ConfirmDialog
        isOpen={!!pendingTabChange}
        onClose={cancelTabChange}
        onConfirm={confirmTabChange}
        title="Ungespeicherte Änderungen"
        message="Du hast ungespeicherte Einstellungen. Möchtest du wirklich wechseln und die Änderungen verwerfen?"
        confirmLabel="Verwerfen"
        cancelLabel="Zurück zu Einstellungen"
        variant="warning"
      />

      {/* Template Context Banner - Show when coming from TemplateEditor */}
      {templateId && templateName && (
        <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-primary-600 dark:text-primary-400">Generiere für Vorlage:</p>
                <p className="font-semibold text-primary-800 dark:text-primary-200">{templateName}</p>
              </div>
            </div>
            {generation.hasQuestions && (
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium flex items-center gap-2 theme-transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {generation.questionCount} Fragen in Vorlage übernehmen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <AlertBanner
          variant="danger"
          message={error}
          onDismiss={() => setError('')}
          className="mb-6"
        />
      )}

      {/* StatBar */}
      <StatBar stats={stats} className="mb-6" />

      {/* Tab Content */}
      {activeTab === 'simple' && (
        <LLMGenerateTab
          llmProvider={config.llmProvider}
          uploadedFiles={fileUpload.uploadedFiles}
          fileContents={fileUpload.fileContents}
          onFileUpload={handleFileUpload}
          onRemoveFile={fileUpload.removeFile}
          formatFileSize={formatFileSize}
          
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          includeCodeBlocks={includeCodeBlocks}
          setIncludeCodeBlocks={setIncludeCodeBlocks}
          multipleChoiceMode={multipleChoiceMode}
          setMultipleChoiceMode={setMultipleChoiceMode}
          appendToExisting={appendToExisting}
          setAppendToExisting={setAppendToExisting}
          existingQuestionsCount={generation.questionCount}
          userGuidance={userGuidance}
          setUserGuidance={setUserGuidance}
          topicTags={tags.topicTags}
          currentTag={tags.currentTag}
          setCurrentTag={tags.setCurrentTag}
          onAddTopicTag={tags.addTag}
          onRemoveTopicTag={tags.removeTag}
          
          // PDF processing mode props
          pdfProcessingMode={pdfProcessingMode}
          setPdfProcessingMode={setPdfProcessingMode}
          imageQuality={imageQuality}
          setImageQuality={setImageQuality}
          hasPdfFiles={hasPdfFiles}
          supportsVision={modelSupportsVision}
          
          selectedTemplateId={config.selectedTemplateId}
          onTemplateSelect={handleTemplateSelect}
          
          isGenerating={generation.isGenerating}
          generationLog={generation.generationLog}
          apiKey={config.apiKey}
          onGenerate={handleGenerate}
        />
      )}

      {activeTab === 'wizard' && (
        <WizardMode
          settings={{
            provider: config.llmProvider,
            apiKey: config.apiKey,
            model: config.model,
            baseUrl: config.baseUrl,
            ollamaUrl: config.baseUrl,
            temperature: config.temperature,
            maxTokens: config.maxTokens
          }}
          onModeToggle={() => setActiveTab('simple')}
          onExportQuestions={(questions) => {
            generation.setGeneratedQuestions(prev => [...prev, ...questions]);
            setActiveTab('preview');
          }}
        />
      )}

      {activeTab === 'preview' && (
        <LLMPreviewTab
          generatedQuestions={generation.generatedQuestions}
          setGeneratedQuestions={generation.setGeneratedQuestions}
          validationErrors={validation.validationErrors}
          editingIndex={editor.editingIndex}
          editedQuestion={editor.editedQuestion}
          setEditedQuestion={editor.setEditedQuestion}
          
          getQualityScore={validation.getQualityScore}
          findDuplicates={validation.findDuplicates}
          validateAllQuestions={validation.validateAllQuestions}
          onRerollQuestion={handleRerollQuestion}
          onRephraseQuestion={handleRephraseQuestion}
          onGenerateVariation={handleGenerateVariation}
          onToggleMultipleChoice={operations.toggleMultipleChoice}
          onShuffleAnswers={operations.shuffleAnswers}
          onStartEditing={editor.startEditing}
          onSaveEdit={editor.saveEdit}
          onCancelEdit={editor.cancelEdit}
          onSaveToSession={handleSaveToSession}
          onDownloadQuestions={handleDownloadQuestions}
          onRefineQuestion={openRefinementChat}
          isGenerating={generation.isGenerating || operations.isProcessing}
          
          existingSessions={persistence.existingSessions}
          selectedSession={persistence.selectedSession}
          setSelectedSession={persistence.setSelectedSession}
          
          sourceTemplateId={templateId}
          sourceTemplateName={templateName}
        />
      )}

      {activeTab === 'settings' && (
        <LLMSettingsTab
          llmProvider={config.llmProvider}
          setLlmProvider={config.setLlmProvider}
          apiKey={config.apiKey}
          setApiKey={config.setApiKey}
          baseUrl={config.baseUrl}
          setBaseUrl={config.setBaseUrl}
          model={config.model}
          setModel={config.setModel}
          temperature={config.temperature}
          setTemperature={config.setTemperature}
          maxTokens={config.maxTokens}
          setMaxTokens={config.setMaxTokens}
          useStructuredOutputs={config.useStructuredOutputs}
          setUseStructuredOutputs={config.setUseStructuredOutputs}
          enableJsonRepair={config.enableJsonRepair}
          setEnableJsonRepair={config.setEnableJsonRepair}
          useConversationHistory={config.useConversationHistory}
          setUseConversationHistory={config.setUseConversationHistory}
          
          selectedTemplateId={config.selectedTemplateId}
          setSelectedTemplateId={config.setSelectedTemplateId}
          
          systemPrompt={config.systemPrompt}
          setSystemPrompt={config.setSystemPrompt}
          getDefaultSystemPrompt={getDefaultSystemPromptUtil}
          
          onSaveSettings={config.saveSettings}
          onResetSettings={() => setShowResetConfirm(true)}
          onUnsavedChangesChange={setHasUnsavedSettings}
          
          userId={user?.uid}
        />
      )}

      {/* Reset Settings Confirmation */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleResetSettings}
        title="Einstellungen zurücksetzen?"
        message="Alle LLM-Einstellungen werden auf die Standardwerte zurückgesetzt."
        confirmLabel="Zurücksetzen"
        cancelLabel="Abbrechen"
        variant="warning"
      />

      {/* Prompt Clipboard Modal */}
      <PromptClipboardModal
        isOpen={showPromptModal}
        onClose={() => setShowPromptModal(false)}
        systemPrompt={clipboardPrompt.system}
        userPrompt={clipboardPrompt.user}
      />
    </ViewLayout2>
  );
}
